import React from 'react';
import { MapPin, FileText } from 'lucide-react';
import { ReceiptExtraInfo } from './ReceiptExtraInfo';

interface ReceiptLineCardProps {
  /** Varenummer — primær identifikator, alltid synlig. */
  itemNo: string;
  /** Varebeskrivelse. Klippes til to linjer; full tekst ligger i redigeringsmodalen. */
  description: string;
  /** Ankommet/mottatt antall for denne raden. */
  quantity: number;
  /**
   * Bestilt antall. Verdier <= 0 skjuler forholdstallet — virtuelle linjer i fritt mottak
   * og bilmottak har ingen reell bestilt-mengde å måle mot.
   */
  orderedQuantity?: number;
  /** Enhetstekst (stk, kg, ...). Vises én gang, etter antallet. */
  unit?: string | null;
  /** Ankomstlokasjon. Utelates for fullførte linjer der lokasjonen ikke lenger er relevant. */
  location?: string | null;
  /** ERP-ordrenummer. Kun aktuelt i modi som blander flere innkjøpsordrer. */
  orderNo?: string | number | null;
  /** Serienummer fra transaksjonen (ItemType=6). */
  serialNo?: string | null;
  /** Profil-konfigurert tilleggsinfo fra innkjøpsordrelinjen. */
  extraInfoValues?: string[];
  /**
   * 'arrived'   — ankomstregistrert, farge viser avvik mot bestilt (blå delvis / grønn eksakt / gul overmottak)
   * 'completed' — fullført mottak, alltid grønn
   */
  variant?: 'arrived' | 'completed';
  /** Åpner redigeringsmodalen. Uten handler rendres kortet som et passivt element. */
  onClick?: () => void;
  /**
   * Kontroller under kortinnholdet (etikettutskrift). Rendres UTENFOR klikkflaten —
   * interaktive felt kan ikke ligge inne i en <button>, og et trykk i feltet skal ikke
   * åpne redigeringsmodalen.
   */
  actions?: React.ReactNode;
}

/**
 * ReceiptLineCard — kortvisning av én ankomstregistrert/fullført mottakslinje.
 *
 * Brukes under `lg` i varemottak-detalj, der tabellvisningen med 5-6 kolonner tvinger
 * horisontal scrolling på håndholdte terminaler (Zebra/Honeywell, ~360 px). Kortet viser
 * de samme feltene som tabellen, men slår «Bestilt» og «Ankommet» sammen til ett
 * forholdstall — det er slik operatøren faktisk leser dem, og det gjør avvik tydeligere
 * enn to tall i hver sin ende av en rad.
 *
 * Desktop (>= lg) beholder tabellen uendret.
 */
export const ReceiptLineCard: React.FC<ReceiptLineCardProps> = ({
  itemNo,
  description,
  quantity,
  orderedQuantity = 0,
  unit,
  location,
  orderNo,
  serialNo,
  extraInfoValues,
  variant = 'arrived',
  onClick,
  actions,
}) => {
  const showRatio = orderedQuantity > 0;

  // Fargekoding av antall-badgen. Fullførte linjer er alltid grønne; for ankomstregistrerte
  // linjer signaliserer fargen avviket mot bestilt mengde.
  const badgeClass = (() => {
    if (variant === 'completed') return 'bg-green-100 text-green-800';
    if (showRatio && quantity > orderedQuantity) return 'bg-amber-100 text-amber-800';
    if (showRatio && quantity >= orderedQuantity) return 'bg-green-100 text-green-800';
    return 'bg-blue-100 text-blue-800';
  })();

  const hasChips = Boolean(location || orderNo || serialNo);

  const content = (
    <>
      {/* Rad 1: varenr + antall. De to feltene operatøren verifiserer etter hver skanning. */}
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-gray-900 truncate">{itemNo}</span>
        <span
          className={`flex-shrink-0 inline-flex items-baseline gap-1 px-2 py-0.5 rounded-full text-sm font-semibold ${badgeClass}`}
        >
          <span>{quantity}</span>
          {showRatio && <span className="font-normal opacity-75">/ {orderedQuantity}</span>}
          {unit && <span className="text-xs font-normal opacity-75">{unit}</span>}
        </span>
      </div>

      {/* Rad 2: beskrivelse */}
      <div className="mt-0.5 text-xs text-gray-600 line-clamp-2 break-words">{description}</div>

      {/* Rad 3: kontekst-chips */}
      {hasChips && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {/* Chips bruker ramme + bg-gray-50, ikke bg-gray-100: i mørkt tema er bg-gray-100 og
              bg-white samme farge, så en ren fyllfarge ville gjort chipsen usynlig. */}
          {location && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 bg-gray-50 text-xs text-gray-700 max-w-full">
              <MapPin className="h-3 w-3 flex-shrink-0 text-gray-500" />
              <span className="truncate">{location}</span>
            </span>
          )}
          {orderNo && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 bg-gray-50 text-xs text-gray-700 max-w-full">
              <FileText className="h-3 w-3 flex-shrink-0 text-gray-500" />
              <span className="truncate">{orderNo}</span>
            </span>
          )}
          {serialNo && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-purple-200 bg-purple-100 text-xs font-mono text-purple-700 max-w-full">
              <span className="truncate">SN: {serialNo}</span>
            </span>
          )}
        </div>
      )}

      {/* Rad 4: profil-konfigurert tilleggsinfo — kunden har bevisst valgt at denne skal synes i lista */}
      <ReceiptExtraInfo values={extraInfoValues || []} compact className="mt-1.5" />
    </>
  );

  if (actions) {
    return (
      <div className="w-full rounded-lg border border-gray-200 bg-white">
        {onClick ? (
          <button
            type="button"
            onClick={onClick}
            className="w-full min-h-[44px] rounded-t-lg p-3 text-left transition-colors hover:bg-blue-50 active:bg-blue-50"
          >
            {content}
          </button>
        ) : (
          <div className="p-3">{content}</div>
        )}
        <div className="border-t border-gray-200 px-3 py-2">{actions}</div>
      </div>
    );
  }

  if (!onClick) {
    return <div className="w-full rounded-lg border border-gray-200 bg-white p-3">{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full min-h-[44px] rounded-lg border border-gray-200 bg-white p-3 text-left transition-colors hover:bg-blue-50 active:bg-blue-50"
    >
      {content}
    </button>
  );
};

export default ReceiptLineCard;
